/* CLEAR is black binding + Strapworks designs. This decorative preview never changes an order. */
const NS = 'http://www.w3.org/2000/svg';
const wrappers = new Map(), images = new Map();
const reduced = matchMedia('(prefers-reduced-motion: reduce)');
const connection = navigator.connection;
const BLACK = { id: 'BLK', name: 'Stock black', image: null };
const HOLD = 5500;
let catalogPromise, catalog = [BLACK], current = BLACK, cursor = 0;
let paused = reduced.matches || !!connection?.saveData;
let timer = 0, generation = 0, serial = 0, loading = false;

function svgElement(name, attrs = {}) {
  const el = document.createElementNS(NS, name);
  for (const [key, value] of Object.entries(attrs)) el.setAttribute(key, value);
  return el;
}
function textureURL(host, pattern) {
  return new URL(pattern.image, new URL(host.dataset.clearTexturesBase, location.href)).href;
}
function visibleStates() {
  return [...wrappers.values()].filter(s => s.visible && s.host.isConnected && s.host.getClientRects().length);
}
function schedule() {
  clearTimeout(timer);
  if (!paused && !document.hidden && !loading && visibleStates().length) timer = setTimeout(advance, HOLD);
}
async function getCatalog(host) {
  if (!catalogPromise) {
    catalogPromise = fetch(host.dataset.clearCatalog, { credentials: 'omit' }).then(r => {
      if (!r.ok) throw Error('CLEAR catalog');
      return r.json();
    }).then(data => {
      if (!Array.isArray(data.patterns)) throw Error('CLEAR catalog format');
      const seen = new Set();
      const patterns = data.patterns.filter(p => p.id !== 'BLK' && typeof p.name === 'string' &&
        /^strap-stock-[a-z0-9-]+\.(jpg|png|webp)$/.test(p.image) && !seen.has(p.image) && seen.add(p.image));
      catalog = [BLACK, ...patterns];
      return catalog;
    }).catch(() => { catalogPromise = null; return [BLACK]; });
  }
  return catalogPromise;
}
function loadImage(url) {
  if (!images.has(url)) {
    const pending = new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = () => { images.delete(url); reject(Error('CLEAR texture')); };
      img.src = url;
    });
    images.set(url, pending);
    if (images.size > 4) images.delete(images.keys().next().value);
  }
  return images.get(url);
}
function paint(state, pattern, image, url) {
  if (!state.host.isConnected) return;
  if (!image) {
    state.layers.forEach(layer => { layer.path.style.opacity = '0'; });
  } else {
    const next = 1 - state.active, layer = state.layers[next];
    // Supplier thumbnails include a second-scale swatch below 68%.
    // Cover this entire lockup with one clean upper swatch, without stretching.
    const cleanHeight = image.naturalHeight * .68;
    const scale = Math.max(252 / image.naturalWidth, 98.25 / cleanHeight);
    const width = image.naturalWidth * scale, height = image.naturalHeight * scale;
    layer.image.setAttribute('width', width);
    layer.image.setAttribute('height', height);
    layer.image.setAttribute('x', (252 - width) / 2);
    layer.image.setAttribute('y', (98.25 - cleanHeight * scale) / 2);
    layer.image.setAttribute('href', url);
    layer.path.style.opacity = '1';
    state.layers[state.active].path.style.opacity = '0';
    state.active = next;
  }
  state.host.dataset.clearPattern = pattern.id;
  state.host.title = 'CLEAR · ' + pattern.name + ' · Strapworks texture preview only';
}
async function showCurrent(state) {
  if (current === BLACK) return paint(state, BLACK);
  const pattern = current, url = textureURL(state.host, pattern);
  try {
    const image = await loadImage(url);
    if (current === pattern && state.visible) paint(state, pattern, image, url);
  } catch { paint(state, BLACK); }
}
async function advance() {
  if (paused || document.hidden || !visibleStates().length) return schedule();
  const token = generation, states = visibleStates();
  loading = true;
  try {
    await getCatalog(states[0].host);
    cursor = (cursor + 1) % catalog.length;
    const pattern = catalog[cursor];
    const url = pattern.image ? textureURL(states[0].host, pattern) : null;
    const image = url ? await loadImage(url) : null;
    if (token !== generation || paused || document.hidden) return;
    current = pattern;
    visibleStates().forEach(state => paint(state, pattern, image, url));
  } catch {
    // Retain the last successfully shown texture. Failed swatches do not blank the wordmark.
  } finally { loading = false; schedule(); }
}
const observer = new IntersectionObserver(entries => {
  for (const entry of entries) {
    const state = wrappers.get(entry.target);
    if (!state) continue;
    state.visible = entry.isIntersecting;
    if (state.visible) showCurrent(state);
  }
  schedule();
}, { threshold: 0.05 });

function init(host) {
  if (wrappers.has(host)) return;
  const svg = host.querySelector('svg'), word = svg?.querySelector('.gf-clear-wordmark__word');
  const bar = svg?.querySelector('.gf-clear-wordmark__bar');
  if (!word || !bar || !host.dataset.clearCatalog || !host.dataset.clearTexturesBase) return;
  const defs = svgElement('defs');
  svg.prepend(defs);
  const id = 'gf-clear-texture-' + (++serial);
  const layers = [0, 1].map(i => {
    const pattern = svgElement('pattern', { id: id + '-' + i, patternUnits: 'userSpaceOnUse', x: 0, y: 0, width: 252, height: 98.25 });
    const image = svgElement('image', { x: 0, y: 0, width: 252, height: 98.25, preserveAspectRatio: 'xMidYMid meet' });
    pattern.append(image); defs.append(pattern);
    const path = svgElement('g', { class: 'gf-clear-wordmark__texture', 'aria-hidden': 'true' });
    for (const base of [word, bar]) {
      const shape = base.cloneNode();
      shape.setAttribute('class', 'gf-clear-wordmark__texture-shape');
      shape.setAttribute('fill', 'url(#' + id + '-' + i + ')');
      shape.setAttribute('stroke', 'none');
      shape.setAttribute('stroke-width', '0');
      path.append(shape);
    }
    path.style.opacity = '0'; svg.append(path);
    return { pattern, image, path };
  });
  // Keep the fine black edge above both fading layers for low-contrast prints.
  for (const [base, width] of [[word, .9], [bar, .65]]) {
    const edge = base.cloneNode();
    edge.setAttribute('class', 'gf-clear-wordmark__edge');
    edge.setAttribute('aria-hidden', 'true');
    edge.setAttribute('fill', 'none');
    edge.setAttribute('stroke', '#101110');
    edge.setAttribute('stroke-width', width);
    edge.setAttribute('stroke-linejoin', 'round');
    svg.append(edge);
  }
  // Remove legacy controls if a cached or dynamically inserted wrapper still has one.
  host.querySelector('[data-clear-motion]')?.remove();
  const state = { host, svg, layers, active: 0, visible: false };
  wrappers.set(host, state); paint(state, BLACK); observer.observe(host);
}
function scan(root) {
  if (root instanceof Element && root.matches('.gf-clear-brand')) init(root);
  root.querySelectorAll?.('.gf-clear-brand').forEach(init);
}
scan(document);
new MutationObserver(records => {
  for (const record of records) record.addedNodes.forEach(scan);
  for (const [host] of wrappers) if (!host.isConnected) { observer.unobserve(host); wrappers.delete(host); }
}).observe(document.body, { childList: true, subtree: true });
document.addEventListener('visibilitychange', () => { generation++; schedule(); });
function syncMotionPreference() {
  paused = reduced.matches || !!connection?.saveData;
  generation++;
  if (paused) { current = BLACK; cursor = 0; wrappers.forEach(s => paint(s, BLACK)); }
  schedule();
}
reduced.addEventListener('change', syncMotionPreference);
connection?.addEventListener?.('change', syncMotionPreference);
